import {
  BuildingIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ChevronDownIcon,
  CreditCardIcon,
  DownloadIcon,
  ExternalLinkIcon,
  FileTextIcon,
  ReceiptTextIcon,
  UsersIcon,
  WalletIcon,
  XIcon,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type { TuitionDocumentSnapshot } from "../shared/tuitionData";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";
import {
  formatCurrency,
  formatCurrencyWithSign,
  type ParentTuitionCheckoutRequest,
  type ParentTuitionDialogId,
  type ParentTuitionPaymentMethodView,
  type ParentTuitionStudentView,
  type ParentTuitionTransactionFilterKey,
  type ParentTuitionViewModel,
} from "./parentTuitionViewModel";

type ParentTuitionSurfaceProps = {
  model: ParentTuitionViewModel;
  activeDialog: ParentTuitionDialogId | null;
  busyAction: string | null;
  errorMessage: string | null;
  hostedReturnNotice?: ReactNode;
  onOpenDialog: (dialogId: ParentTuitionDialogId) => void;
  onCloseDialog: () => void;
  onStartPayment: (request: ParentTuitionCheckoutRequest) => void;
  onOpenBillingPortal: () => void;
  onDownloadDocument: (
    document: TuitionDocumentSnapshot,
    busyId: string,
  ) => void;
};

type DialogStep = "select" | "ready";

const installmentStatusDotClasses: Record<
  "success" | "warning" | "danger" | "muted",
  string
> = {
  success: "bg-aquamarine",
  warning: "bg-coral",
  danger: "bg-strawberry",
  muted: "bg-text-tertiary",
};

const progressIconToneClasses: Record<
  "success" | "warning" | "danger",
  string
> = {
  success: "bg-success-background text-aquamarine",
  warning: "bg-info-background text-primary",
  danger: "bg-error-background text-strawberry",
};

const transactionToneByType: Record<
  ParentTuitionTransactionFilterKey,
  "success" | "warning" | "danger" | "muted"
> = {
  all: "muted",
  charge: "muted",
  payment: "success",
  credit: "success",
  late_fee: "danger",
  adjustment: "muted",
};

const DialogShell = ({
  title,
  onClose,
  children,
  maxWidthClass = "max-w-lg",
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidthClass?: string;
}) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
    role="dialog"
    aria-modal="true"
  >
    <button
      type="button"
      className="absolute inset-0 bg-iron-grey/40 backdrop-blur-sm"
      aria-hidden="true"
      tabIndex={-1}
      onClick={onClose}
    />
    <div
      className={`relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-[16px] bg-surface-light shadow-lg ${maxWidthClass}`}
    >
      <div className="flex items-center justify-between border-b border-border p-6 shrink-0">
        <h2 className="font-heading text-[20px] font-semibold text-iron-grey">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${title}`}
          className="text-text-secondary transition-colors hover:text-iron-grey"
        >
          <XIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="overflow-y-auto p-6">{children}</div>
    </div>
  </div>
);

const StatusBadge = ({
  tone,
  label,
  detail,
}: {
  tone: "success" | "warning" | "danger" | "muted";
  label: string;
  detail?: string | null;
}) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-2">
      <span
        className={`h-2 w-2 rounded-full ${installmentStatusDotClasses[tone]}`}
        aria-hidden="true"
      />
      <span className="font-body text-[14px] text-iron-grey">{label}</span>
    </div>
    {detail ? (
      <span className="ml-4 mt-0.5 text-[13px] text-text-secondary">
        {detail}
      </span>
    ) : null}
  </div>
);

const StudentBreakdownCard = ({
  student,
  expanded,
  onToggle,
}: {
  student: ParentTuitionStudentView;
  expanded: boolean;
  onToggle: () => void;
}) => {
  const hasFinancialAid = student.breakdown.some((line) =>
    /financial aid/i.test(line.label),
  );
  const rows = [
    ...student.breakdown,
    ...(hasFinancialAid
      ? []
      : [
          {
            id: `${student.id}:financial-aid`,
            label: "Financial Aid",
            amountCents: 0,
            tone: "aid" as const,
          },
        ]),
  ];

  return (
    <div
      data-testid={`parent-tuition-student-breakdown-card-${student.id}`}
      className="mb-4 overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-[0_1px_3px_rgba(0,0,0,0.1)] transition-all duration-200"
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`parent-tuition-student-breakdown-toggle-${student.id}`}
        className="flex w-full flex-row items-center justify-between p-4 text-left transition-colors hover:bg-muted sm:flex-row sm:items-center"
        aria-expanded={expanded}
      >
        <div className="mb-2 flex flex-row items-center justify-start gap-3 sm:mb-0">
          <ChevronDownIcon
            className={`h-5 w-5 text-text-secondary transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
          />
          <div>
            <p className="font-heading text-[16px] font-semibold text-iron-grey">
              {student.name}
            </p>
            <p className="font-body text-[13px] text-text-secondary">
              {student.gradeLabel}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-6 pl-8 sm:justify-end sm:gap-8 sm:pl-0">
          <div className="hidden text-right sm:block">
            <p className="mb-0.5 font-title text-[12px] uppercase tracking-wide text-text-secondary">
              Tuition
            </p>
            <p className="font-body text-[14px] text-iron-grey">
              {student.tuitionLabel}
            </p>
          </div>
          <div className="hidden text-right sm:block">
            <p className="mb-0.5 font-title text-[12px] uppercase tracking-wide text-text-secondary">
              Fees
            </p>
            <p className="font-body text-[14px] text-iron-grey">
              {student.feesLabel}
            </p>
          </div>
          <div className="text-right">
            <p className="mb-0.5 font-title text-[12px] uppercase tracking-wide text-text-secondary">
              Subtotal
            </p>
            <p className="font-body text-[14px] font-semibold text-iron-grey">
              {student.subtotalLabel}
            </p>
          </div>
        </div>
      </button>

      {expanded ? (
        <div
          data-testid={`parent-tuition-student-breakdown-panel-${student.id}`}
          className="border-t border-border bg-background-light px-4 pb-4 pt-2"
        >
          <table className="w-full border-collapse text-left">
            <tbody>
              {rows.map((line, index) => {
                const amountLabel =
                  line.tone === "aid"
                    ? `-${formatCurrency(Math.abs(line.amountCents))}`
                    : formatCurrencyWithSign(line.amountCents);
                const textClass =
                  line.tone === "aid"
                    ? "text-primary"
                    : line.tone === "credit"
                      ? "text-jungle"
                      : "text-iron-grey";

                return (
                  <tr
                    key={line.id}
                    className="border-b border-border last:border-0"
                  >
                    <td
                      data-testid={`parent-tuition-student-breakdown-line-label-${student.id}-${index + 1}`}
                      className={`py-3 font-body text-[14px] ${line.tone === "charge" ? "pl-4 text-text-secondary" : ""} ${textClass}`}
                    >
                      {line.label}
                    </td>
                    <td
                      data-testid={`parent-tuition-student-breakdown-line-amount-${student.id}-${index + 1}`}
                      className={`py-3 text-right font-body text-[14px] ${line.tone === "charge" ? "text-text-secondary" : ""} ${textClass}`}
                    >
                      {amountLabel}
                    </td>
                  </tr>
                );
              })}
              <tr
                className="border-t-2 border-border"
                data-testid={`parent-tuition-student-breakdown-net-total-${student.id}`}
              >
                <td className="py-3 font-body text-[14px] font-semibold text-iron-grey">
                  Net total
                </td>
                <td className="py-3 text-right font-body text-[14px] font-semibold text-iron-grey">
                  {student.subtotalLabel}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const PaymentMethodIcon = ({
  method,
}: {
  method: ParentTuitionPaymentMethodView;
}) =>
  method.detail.toLowerCase().includes("bank") ? (
    <BuildingIcon className="h-5 w-5 text-iron-grey" />
  ) : (
    <CreditCardIcon className="h-5 w-5 text-iron-grey" />
  );

export function ParentTuitionSurface({
  model,
  activeDialog,
  busyAction,
  errorMessage,
  hostedReturnNotice,
  onOpenDialog,
  onCloseDialog,
  onStartPayment,
  onOpenBillingPortal,
  onDownloadDocument,
}: ParentTuitionSurfaceProps) {
  const [expandedStudentId, setExpandedStudentId] = useState<string | null>(
    null,
  );
  const [transactionFilter, setTransactionFilter] =
    useState<ParentTuitionTransactionFilterKey>("all");
  const [visibleTransactionCount, setVisibleTransactionCount] = useState(10);
  const [paymentSelectedInstallmentIds, setPaymentSelectedInstallmentIds] =
    useState<string[]>([]);
  const [paymentSelectedMethodId, setPaymentSelectedMethodId] =
    useState<string>("");
  const [statementStep, setStatementStep] = useState<DialogStep>("select");
  const [selectedStatementDocumentId, setSelectedStatementDocumentId] =
    useState<string>("");
  const [receiptStep, setReceiptStep] = useState<DialogStep>("select");
  const [selectedReceiptDocumentId, setSelectedReceiptDocumentId] =
    useState<string>("");

  useEffect(() => {
    if (activeDialog !== "payment") {
      return;
    }

    const firstActionableInstallment =
      model.installments.find(
        (item) =>
          item.installment.remainingAmountCents > 0 &&
          item.installment.status !== "upcoming",
      ) ??
      model.installments.find(
        (item) => item.installment.remainingAmountCents > 0,
      ) ??
      null;
    setPaymentSelectedInstallmentIds(
      firstActionableInstallment
        ? [firstActionableInstallment.installment.id]
        : [],
    );
    setPaymentSelectedMethodId(
      model.primaryPaymentMethod?.id ?? model.paymentMethods[0]?.id ?? "",
    );
  }, [
    activeDialog,
    model.installments,
    model.paymentMethods,
    model.primaryPaymentMethod,
  ]);

  useEffect(() => {
    if (activeDialog !== "statement") {
      return;
    }
    setStatementStep("select");
    setSelectedStatementDocumentId(model.statementDocuments[0]?.id ?? "");
  }, [activeDialog, model.statementDocuments]);

  useEffect(() => {
    if (activeDialog !== "receipt") {
      return;
    }
    setReceiptStep("select");
    setSelectedReceiptDocumentId(model.receiptDocuments[0]?.id ?? "");
  }, [activeDialog, model.receiptDocuments]);

  const filteredTransactions = useMemo(
    () =>
      model.transactions.filter(
        (transaction) =>
          transactionFilter === "all" ||
          transaction.typeKey === transactionFilter,
      ),
    [model.transactions, transactionFilter],
  );
  const visibleTransactions = filteredTransactions.slice(
    0,
    visibleTransactionCount,
  );
  const unpaidInstallments = useMemo(
    () =>
      model.installments.filter(
        (item) => item.installment.remainingAmountCents > 0,
      ),
    [model.installments],
  );
  const payableInstallments = useMemo(
    () =>
      unpaidInstallments.filter(
        (item) => item.installment.status !== "upcoming",
      ),
    [unpaidInstallments],
  );
  const paymentSelectedInstallmentSet = useMemo(
    () => new Set(paymentSelectedInstallmentIds),
    [paymentSelectedInstallmentIds],
  );
  const selectedPaymentRows = useMemo(
    () =>
      unpaidInstallments.filter((item) =>
        paymentSelectedInstallmentSet.has(item.installment.id),
      ),
    [paymentSelectedInstallmentSet, unpaidInstallments],
  );
  const paymentSubtotalCents = selectedPaymentRows.reduce(
    (sum, item) => sum + item.installment.remainingAmountCents,
    0,
  );
  const allUnpaidIds = unpaidInstallments.map((item) => item.installment.id);
  const isPayInFull =
    allUnpaidIds.length > 0 &&
    allUnpaidIds.every((id) => paymentSelectedInstallmentSet.has(id));
  const selectedStatementDocument =
    model.statementDocuments.find(
      (document) => document.id === selectedStatementDocumentId,
    ) ??
    model.statementDocuments[0] ??
    null;
  const selectedReceiptDocument =
    model.receiptDocuments.find(
      (document) => document.id === selectedReceiptDocumentId,
    ) ??
    model.receiptDocuments[0] ??
    null;
  const selectedReceiptDetailParts = (
    selectedReceiptDocument?.detail ?? ""
  ).split(" • ");
  const selectedReceiptIssuedLabel =
    selectedReceiptDetailParts[selectedReceiptDetailParts.length - 1] ||
    "Available now";
  const primaryPayor =
    model.payers.find((payer) => payer.isPrimary) ?? model.payers[0] ?? null;
  const secondaryPayors = primaryPayor
    ? model.payers.filter((payer) => payer.id !== primaryPayor.id)
    : [];

  const togglePaymentInstallment = (installmentId: string) => {
    setPaymentSelectedInstallmentIds((current) =>
      current.includes(installmentId)
        ? current.filter((id) => id !== installmentId)
        : [...current, installmentId],
    );
  };

  const togglePayInFull = () => {
    setPaymentSelectedInstallmentIds(isPayInFull ? [] : allUnpaidIds);
  };

  const handlePaymentSubmit = () => {
    if (paymentSelectedInstallmentIds.length === 0) {
      return;
    }

    if (isPayInFull) {
      onStartPayment({ kind: "full" });
      return;
    }

    if (paymentSelectedInstallmentIds.length === 1) {
      onStartPayment({
        kind: "installment",
        installmentId: paymentSelectedInstallmentIds[0]!,
      });
      return;
    }

    if (
      model.overdueAmountCents > 0 &&
      paymentSubtotalCents === model.overdueAmountCents
    ) {
      onStartPayment({ kind: "overdue" });
      return;
    }

    onStartPayment({ kind: "custom", amountCents: paymentSubtotalCents });
  };

  return (
    <div
      data-testid="parent-tuition-source-faithful-root"
      data-route-family="internal-owned"
      data-route-ready="true"
      data-payment-operation-key="tuition_checkout"
      data-daf-recommendation-scope="not-allowed"
      className="source-faithful-shell"
      style={{ "--ystnj-source-faithful-max-width": "1024px" } as CSSProperties}
    >
      <div className="mx-auto max-w-4xl px-4 py-8 pb-20 sm:px-6">
        <div className="mb-8 flex flex-col justify-end gap-4 sm:flex-row sm:items-center">
          <select
            value={model.academicYearValue}
            disabled
            aria-label="Academic year"
            className="w-full rounded-[6px] border border-border-strong bg-surface-light px-3 pb-[8px] pt-[8px] font-body text-[14px] text-iron-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto"
          >
            <option value={model.academicYearValue}>
              {model.academicYearLabel}
            </option>
          </select>
        </div>

        <section
          data-testid="parent-tuition-summary-kpis"
          className="mb-[16px] rounded-[12px] border border-border bg-surface-light p-6 shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
        >
          <div className="mb-3 flex items-end justify-between">
            <div>
              <p className="mb-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                Total tuition &amp; fees
              </p>
              <p className="font-heading text-[14px] font-semibold text-iron-grey">
                {model.progress.totalTuitionLabel}
              </p>
            </div>
            <div className="text-right">
              <p className="mb-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                Credits applied
              </p>
              <p className="font-body text-[14px] text-iron-grey">
                -{model.progress.creditsAppliedLabel}
              </p>
            </div>
          </div>

          <div className="relative mb-3 h-3 overflow-hidden rounded-full bg-muted">
            <div
              className="absolute left-0 top-0 h-full bg-primary transition-all duration-500 ease-out"
              style={{ width: `${model.progress.progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={model.progress.progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>

          <div className="mb-8 flex items-start justify-between">
            <div>
              <p className="mb-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                Total paid
              </p>
              <p className="font-body text-[14px] text-iron-grey">
                {model.progress.totalPaidLabel} &middot;{" "}
                {model.progress.progressPercentage}%
              </p>
            </div>
            <div className="text-right">
              <p className="mb-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                Remaining balance
              </p>
              <p className="font-heading text-[24px] font-semibold text-iron-grey">
                {model.progress.remainingBalanceLabel}
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-between gap-4 rounded-[8px] border border-border bg-background-light p-4 pb-[8px] pt-[8px] sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${progressIconToneClasses[model.progress.nextPaymentTone]}`}
              >
                {model.progress.nextPaymentTone === "success" ? (
                  <CheckCircle2Icon className="h-5 w-5" />
                ) : (
                  <CalendarIcon className="h-5 w-5" />
                )}
              </div>

              <div>
                <p className="font-body text-[14px] text-iron-grey">
                  <span
                    className={`font-semibold ${model.progress.nextPaymentTone === "danger" ? "text-strawberry" : ""}`}
                  >
                    {model.progress.nextPaymentLabel}
                  </span>
                  {model.progress.nextPaymentTone === "danger"
                    ? ` ${model.progress.nextPaymentDetail.replace(/^Past-due balance from\s+/i, "was due ")}`
                    : ""}
                </p>
                {model.progress.nextPaymentTone !== "danger" ? (
                  <p className="mt-0.5 font-body text-[13px] text-text-secondary">
                    {model.progress.nextPaymentDetail}
                  </p>
                ) : null}
                {model.progress.autoPayDetail
                  .toLowerCase()
                  .includes("active") &&
                model.progress.nextPaymentTone !== "danger" ? (
                  <p className="mt-0.5 font-body text-[13px] text-text-secondary">
                    Auto-pay is on. Your next payment will be charged
                    automatically.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        {hostedReturnNotice}

        <div data-testid="parent-tuition-payment-actions" className="mb-8">
          <div
            data-testid="parent-tuition-quick-actions"
            className="flex flex-col gap-3 sm:flex-row"
          >
            <button
              type="button"
              onClick={() => onOpenDialog("payment")}
              data-testid="parent-tuition-make-payment-button"
              className="flex flex-1 items-center justify-center gap-2 rounded-[6px] bg-primary px-4 py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover"
            >
              <CreditCardIcon className="h-4 w-4" />
              Pay now
            </button>
            <button
              type="button"
              onClick={() => onOpenDialog("statement")}
              className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light px-4 py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <FileTextIcon className="h-4 w-4" />
              Statements
            </button>
            <button
              type="button"
              onClick={() => onOpenDialog("receipt")}
              className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light px-4 py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <ReceiptTextIcon className="h-4 w-4" />
              Receipts
            </button>
            <button
              type="button"
              onClick={() => onOpenDialog("payors")}
              className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light px-4 py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <UsersIcon className="h-4 w-4" />
              Payors
            </button>
            <button
              type="button"
              onClick={() => onOpenDialog("methods")}
              data-testid="parent-tuition-manage-payment-methods-button"
              className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light px-4 py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <WalletIcon className="h-4 w-4" />
              Methods
            </button>
          </div>
        </div>

        {errorMessage ? (
          <SupportErrorMessage
            data-testid="parent-tuition-action-error"
            className="mb-8"
          >
            {errorMessage}
          </SupportErrorMessage>
        ) : null}

        <section className="mb-10">
          <h2 className="mb-4 font-heading text-[20px] font-semibold text-iron-grey">
            Per-child breakdown
          </h2>
          <div data-testid="parent-tuition-student-breakdown-list">
            {model.students.length > 0 ? (
              model.students.map((student) => (
                <StudentBreakdownCard
                  key={student.id}
                  student={student}
                  expanded={expandedStudentId === student.id}
                  onToggle={() =>
                    setExpandedStudentId((current) =>
                      current === student.id ? null : student.id,
                    )
                  }
                />
              ))
            ) : (
              <div
                data-testid="parent-tuition-student-breakdown-empty"
                className="rounded-[8px] border border-dashed border-border px-4 py-5 text-[14px] text-text-secondary"
              >
                No student tuition records were returned for this family.
              </div>
            )}
          </div>
        </section>

        <section className="mb-10">
          <div className="mb-4">
            <h2 className="mr-3 inline-block font-heading text-[20px] font-semibold text-iron-grey">
              Installment schedule
            </h2>
            <span className="inline-block rounded-[4px] bg-muted px-2 py-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
              {model.installmentPlanLabel}
            </span>
          </div>

          <div
            data-testid="parent-tuition-installment-schedule"
            className="overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-[0_1px_3px_rgba(0,0,0,0.1)]"
          >
            <div className="overflow-x-auto">
              <table className="min-w-[480px] w-full border-collapse text-left sm:min-w-[600px]">
                <thead>
                  <tr className="border-b border-border bg-muted">
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Period
                    </th>
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Due date
                    </th>
                    <th className="px-2 py-2 text-right font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Amount
                    </th>
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Status
                    </th>
                    <th className="px-2 py-2 text-right font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {model.installments.map((row, index) => (
                    <Fragment key={row.installment.id}>
                      <tr
                        data-testid={`parent-tuition-installment-row-${index + 1}`}
                        className={`border-b border-border transition-colors hover:bg-background-light ${index === model.installments.length - 1 ? "border-0" : ""}`}
                      >
                        <td className="px-2 py-2 font-body text-[12px] text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.installment.label}
                        </td>
                        <td className="px-2 py-2 font-body text-[12px] text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.installment.dueDateLabel}
                        </td>
                        <td className="px-2 py-2 text-right font-body text-[12px] font-medium text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.remainingAmountLabel}
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-4">
                          <StatusBadge
                            tone={row.statusTone}
                            label={row.statusLabel}
                            detail={row.statusDetail}
                          />
                        </td>
                        <td className="px-2 py-2 text-right sm:px-4 sm:py-4">
                          {row.installment.remainingAmountCents > 0 ? (
                            <button
                              type="button"
                              onClick={() => {
                                setPaymentSelectedInstallmentIds([
                                  row.installment.id,
                                ]);
                                onOpenDialog("payment");
                              }}
                              className="rounded-[6px] border border-border-strong bg-surface-light px-2 py-1 font-body text-[12px] font-medium text-iron-grey transition-colors hover:bg-muted sm:px-3 sm:py-1.5 sm:text-[13px]"
                            >
                              Pay
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="mt-3 font-body text-[13px] text-text-secondary">
            Contact the business office to change your payment plan.
          </p>
        </section>

        <section className="mb-10">
          <div className="mb-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <h2 className="font-heading text-[20px] font-semibold text-iron-grey">
              Transaction history
            </h2>
            <button
              type="button"
              onClick={() => onOpenDialog("statement")}
              className="flex items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light px-3 py-2 font-body text-[13px] font-medium text-iron-grey transition-colors hover:bg-muted"
            >
              <DownloadIcon className="h-4 w-4" />
              Download statement
            </button>
          </div>

          <div className="overflow-hidden rounded-[12px] border border-border bg-surface-light shadow-[0_1px_3px_rgba(0,0,0,0.1)]">
            <div className="flex items-center gap-[65px] border-b border-border bg-background-light p-4">
              <label
                htmlFor="parent-tuition-transaction-filter"
                className="font-title text-[12px] uppercase tracking-wide text-text-secondary"
              >
                Filter by type
              </label>
              <select
                id="parent-tuition-transaction-filter"
                value={transactionFilter}
                onChange={(event) => {
                  setTransactionFilter(
                    event.target.value as ParentTuitionTransactionFilterKey,
                  );
                  setVisibleTransactionCount(10);
                }}
                className="rounded-[6px] border border-border-strong bg-surface-light px-3 py-1.5 pr-[12px] font-body text-[14px] text-iron-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {model.transactionFilters.map((filter) => (
                  <option key={filter.id} value={filter.id}>
                    {filter.label}
                  </option>
                ))}
              </select>
            </div>

            <div
              data-testid="parent-tuition-family-statement-table"
              className="overflow-x-auto"
            >
              <table className="min-w-[480px] w-full border-collapse text-left sm:min-w-[600px]">
                <thead>
                  <tr className="border-b border-border bg-muted">
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Date
                    </th>
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Description
                    </th>
                    <th className="px-2 py-2 font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Type
                    </th>
                    <th className="px-2 py-2 text-right font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Amount
                    </th>
                    <th className="px-2 py-2 text-right font-title text-[11px] font-medium uppercase tracking-wide text-text-secondary sm:px-4 sm:py-3 sm:text-[12px]">
                      Balance
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTransactions.length > 0 ? (
                    visibleTransactions.map((row) => (
                      <tr
                        key={row.transaction.id}
                        className="border-b border-border transition-colors last:border-0 hover:bg-background-light"
                      >
                        <td className="whitespace-nowrap px-2 py-2 font-body text-[12px] text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.dateLabel}
                        </td>
                        <td className="px-2 py-2 font-body text-[12px] text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.description}
                        </td>
                        <td className="px-2 py-2 sm:px-4 sm:py-4">
                          <StatusBadge
                            tone={transactionToneByType[row.typeKey]}
                            label={row.typeLabel}
                          />
                        </td>
                        <td
                          className={`whitespace-nowrap px-2 py-2 text-right font-body text-[12px] font-medium sm:px-4 sm:py-4 sm:text-[14px] ${row.amountTone === "positive" ? "text-jungle" : "text-iron-grey"}`}
                        >
                          {row.amountLabel}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 text-right font-body text-[12px] text-iron-grey sm:px-4 sm:py-4 sm:text-[14px]">
                          {row.balanceLabel}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={5}
                        className="py-8 text-center font-body text-[14px] text-text-secondary"
                      >
                        <span data-testid="parent-tuition-family-statement-empty">
                          No transactions found for this filter.
                        </span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredTransactions.length > visibleTransactionCount ? (
              <div className="border-t border-border bg-background-light p-4 text-center">
                <button
                  type="button"
                  onClick={() =>
                    setVisibleTransactionCount((current) => current + 10)
                  }
                  className="font-body text-[14px] font-medium text-primary transition-colors hover:text-primary-hover"
                >
                  Show more transactions
                </button>
              </div>
            ) : null}
          </div>
        </section>

        {activeDialog === "payment" ? (
          <DialogShell
            title="Make a payment"
            onClose={onCloseDialog}
            maxWidthClass="max-w-lg"
          >
            <div
              data-testid="parent-tuition-payment-options"
              className="space-y-6"
            >
              <div>
                <h3 className="mb-3 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                  Select installments
                </h3>
                <div className="overflow-hidden rounded-[8px] border border-border divide-y divide-border">
                  {payableInstallments.length > 0 ? (
                    payableInstallments.map((row) => (
                      <label
                        key={row.installment.id}
                        className="flex cursor-pointer items-start gap-3 p-4 transition-colors hover:bg-background-light"
                      >
                        <input
                          type="checkbox"
                          checked={paymentSelectedInstallmentSet.has(
                            row.installment.id,
                          )}
                          onChange={() =>
                            togglePaymentInstallment(row.installment.id)
                          }
                          className="mt-1 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <span className="font-body text-[14px] font-medium text-iron-grey">
                              {row.installment.label}
                            </span>
                            <span className="font-body text-[14px] font-medium text-iron-grey">
                              {row.remainingAmountLabel}
                            </span>
                          </div>
                          <div className="mt-1 flex items-start justify-between">
                            <span className="font-body text-[13px] text-text-secondary">
                              Due {row.installment.dueDateLabel}
                            </span>
                            {row.installment.status === "overdue" ? (
                              <span className="font-body text-[13px] text-strawberry">
                                Overdue
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-center font-body text-[14px] text-text-secondary">
                      No installments currently due.
                    </div>
                  )}
                  <label
                    className={`flex cursor-pointer items-start gap-3 p-4 transition-colors hover:bg-background-light ${isPayInFull ? "bg-info-background" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPayInFull}
                      onChange={togglePayInFull}
                      className="mt-1 h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                    />
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <span className="font-body text-[14px] font-semibold text-primary">
                          Pay in full
                        </span>
                        <span className="font-body text-[14px] font-semibold text-primary">
                          {model.payInFullLabel}
                        </span>
                      </div>
                      <div className="mt-1 flex items-start justify-between">
                        <span className="font-body text-[13px] text-text-secondary">
                          All remaining open installments
                        </span>
                        <span className="font-body text-[13px] text-jungle">
                          All remaining
                        </span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <h3 className="mb-3 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                  Payment method
                </h3>
                <div className="mb-3 overflow-hidden rounded-[8px] border border-border divide-y divide-border">
                  {model.paymentMethods.length > 0 ? (
                    model.paymentMethods.map((method) => (
                      <label
                        key={method.id}
                        className="flex cursor-pointer items-center gap-3 p-4 transition-colors hover:bg-background-light"
                      >
                        <input
                          type="radio"
                          name="parent-tuition-payment-method"
                          checked={paymentSelectedMethodId === method.id}
                          onChange={() => setPaymentSelectedMethodId(method.id)}
                          className="h-4 w-4 border-border-strong text-primary focus:ring-primary"
                        />
                        <div className="flex flex-1 items-center gap-3">
                          <PaymentMethodIcon method={method} />
                          <span className="font-body text-[14px] text-iron-grey">
                            {method.label}
                          </span>
                        </div>
                      </label>
                    ))
                  ) : (
                    <div className="p-4 text-center font-body text-[14px] text-text-secondary">
                      No saved payment method is on file. Hosted checkout will
                      still collect a payment method.
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onOpenDialog("methods")}
                  className="font-body text-[14px] font-medium text-primary transition-colors hover:text-primary-hover"
                >
                  + Add new method
                </button>
              </div>
            </div>

            <div className="shrink-0 border-t border-border bg-background-light p-6">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-body text-[14px] font-medium text-iron-grey">
                  Total to pay
                </span>
                <span className="font-heading text-[24px] font-semibold text-iron-grey">
                  {formatCurrency(paymentSubtotalCents)}
                </span>
              </div>
              <p className="mb-4 text-center font-body text-[12px] text-text-secondary">
                Hosted checkout confirms the final payment method and amount
                details.
              </p>
              <button
                type="button"
                onClick={handlePaymentSubmit}
                disabled={
                  paymentSelectedInstallmentIds.length === 0 ||
                  (model.paymentMethods.length > 0 &&
                    !paymentSelectedMethodId) ||
                  busyAction !== null
                }
                className="w-full rounded-[6px] bg-primary py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:bg-muted disabled:text-text-tertiary"
              >
                {busyAction?.startsWith("checkout-")
                  ? "Redirecting..."
                  : `Pay ${formatCurrency(paymentSubtotalCents)}`}
              </button>
            </div>
          </DialogShell>
        ) : null}

        {activeDialog === "methods" ? (
          <DialogShell
            title="Payment methods"
            onClose={onCloseDialog}
            maxWidthClass="max-w-lg"
          >
            <div className="space-y-6">
              <div
                data-testid="parent-tuition-autopay-status"
                className="rounded-[8px] border border-border bg-background-light p-4"
              >
                <p className="mb-1 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                  Auto-pay
                </p>
                <p className="font-body text-[14px] font-medium text-iron-grey">
                  {model.billingProfile.autoPayLabel}
                </p>
                <p className="mt-1 font-body text-[13px] text-text-secondary">
                  {model.progress.autoPayDetail}
                </p>
              </div>

              {model.paymentMethods.length > 0 ? (
                <div className="space-y-4">
                  {model.paymentMethods.map((method) => (
                    <div
                      key={method.id}
                      className="flex flex-col justify-between gap-4 rounded-[8px] border border-border p-4 sm:flex-row sm:items-center"
                    >
                      <div className="flex items-start gap-3 sm:items-center">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-light">
                          <PaymentMethodIcon method={method} />
                        </div>
                        <div>
                          <p className="font-body text-[14px] font-medium text-iron-grey">
                            {method.label}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="font-body text-[13px] text-text-secondary">
                              {method.detail}
                            </span>
                            {method.isDefault ? (
                              <span className="rounded-[4px] bg-muted px-2 py-0.5 font-title text-[11px] uppercase tracking-wide text-text-secondary">
                                Default
                              </span>
                            ) : null}
                            {method.isAutoPayMethod ? (
                              <span className="rounded-[4px] bg-info-background px-2 py-0.5 font-title text-[11px] uppercase tracking-wide text-primary">
                                Auto-pay
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="font-body text-[14px] text-text-secondary">
                    No payment methods on file. Add a card or bank account in
                    hosted billing before the next charge.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={onOpenBillingPortal}
                className="w-full rounded-[6px] border border-border-strong bg-surface-light py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted"
              >
                Add payment method
              </button>

              <div className="rounded-[8px] border border-border bg-background-light p-4 text-[13px] text-text-secondary">
                Saved-method changes, removals, and new payment sources still
                happen in the hosted billing portal for this wave.
              </div>

              <button
                type="button"
                onClick={onOpenBillingPortal}
                className="flex w-full items-center justify-center gap-2 rounded-[6px] bg-primary px-4 py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-70"
                disabled={busyAction !== null}
              >
                <ExternalLinkIcon className="h-4 w-4" />
                {busyAction === "portal"
                  ? "Opening portal..."
                  : "Open hosted billing portal"}
              </button>
            </div>
          </DialogShell>
        ) : null}

        {activeDialog === "statement" ? (
          <DialogShell
            title="Statements"
            onClose={onCloseDialog}
            maxWidthClass="max-w-md"
          >
            <div className="space-y-6">
              {statementStep === "select" ? (
                <>
                  <div>
                    <label
                      htmlFor="parent-tuition-statement-select"
                      className="mb-2 block font-body text-[13px] text-iron-grey"
                    >
                      Select statement
                    </label>
                    <select
                      id="parent-tuition-statement-select"
                      data-testid="parent-tuition-statement-select"
                      value={selectedStatementDocumentId}
                      onChange={(event) =>
                        setSelectedStatementDocumentId(event.target.value)
                      }
                      className="w-full rounded-[6px] border border-border-strong bg-surface-light px-3 py-2.5 font-body text-[14px] text-iron-grey focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {model.statementDocuments.length > 0 ? (
                        model.statementDocuments.map((document) => (
                          <option key={document.id} value={document.id}>
                            {document.label}
                          </option>
                        ))
                      ) : (
                        <option value="">
                          No statement documents available
                        </option>
                      )}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={() => setStatementStep("ready")}
                    disabled={!selectedStatementDocument}
                    className="w-full rounded-[6px] bg-primary py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:bg-muted disabled:text-text-tertiary"
                  >
                    Generate statement
                  </button>
                </>
              ) : (
                <div className="flex flex-col items-center py-4 text-center">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-info-background">
                    <FileTextIcon className="h-8 w-8 text-primary" />
                  </div>
                  <p className="mb-1 font-heading text-[18px] font-semibold text-iron-grey">
                    Statement ready
                  </p>
                  <p className="mb-8 font-body text-[14px] text-text-secondary">
                    {selectedStatementDocument?.label ?? "Family statement"}
                  </p>

                  <div className="flex w-full gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        selectedStatementDocument
                          ? onDownloadDocument(
                              selectedStatementDocument.document,
                              "statement-modal-print",
                            )
                          : undefined
                      }
                      className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted disabled:opacity-70"
                      disabled={
                        !selectedStatementDocument || busyAction !== null
                      }
                    >
                      <FileTextIcon className="h-4 w-4" />
                      {busyAction === "statement-modal-print"
                        ? "Preparing..."
                        : "Print"}
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        selectedStatementDocument
                          ? onDownloadDocument(
                              selectedStatementDocument.document,
                              "statement-modal-download",
                            )
                          : undefined
                      }
                      className="flex flex-1 items-center justify-center gap-2 rounded-[6px] bg-primary py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-70"
                      disabled={
                        !selectedStatementDocument || busyAction !== null
                      }
                    >
                      <DownloadIcon className="h-4 w-4" />
                      {busyAction === "statement-modal-download"
                        ? "Preparing..."
                        : "Download"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </DialogShell>
        ) : null}

        {activeDialog === "receipt" ? (
          <DialogShell
            title={
              receiptStep === "ready" ? "Receipt preview" : "Generate receipt"
            }
            onClose={onCloseDialog}
            maxWidthClass={receiptStep === "ready" ? "max-w-3xl" : "max-w-md"}
          >
            <div className="space-y-6">
              {receiptStep === "select" ? (
                <>
                  <div>
                    <h3 className="mb-3 font-title text-[12px] uppercase tracking-wide text-text-secondary">
                      Select payment
                    </h3>
                    <div
                      data-testid="parent-tuition-receipt-inventory"
                      className="overflow-hidden rounded-[8px] border border-border divide-y divide-border"
                    >
                      {model.receiptDocuments.length > 0 ? (
                        model.receiptDocuments.map((document) => (
                          <label
                            key={document.id}
                            className="flex cursor-pointer items-start gap-3 p-4 transition-colors hover:bg-background-light"
                          >
                            <input
                              type="radio"
                              name="parent-tuition-receipt"
                              checked={
                                selectedReceiptDocumentId === document.id
                              }
                              onChange={() =>
                                setSelectedReceiptDocumentId(document.id)
                              }
                              className="mt-1 h-4 w-4 border-border-strong text-primary focus:ring-primary"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <span className="font-body text-[14px] font-medium text-iron-grey">
                                  {document.badgeLabel}
                                </span>
                                <span className="font-body text-[14px] font-medium text-iron-grey">
                                  {document.label}
                                </span>
                              </div>
                              <div className="mt-1">
                                <span className="font-body text-[13px] text-text-secondary">
                                  {document.detail}
                                </span>
                              </div>
                            </div>
                          </label>
                        ))
                      ) : (
                        <div className="p-4 text-center font-body text-[14px] text-text-secondary">
                          No payments available for receipts.
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setReceiptStep("ready")}
                    disabled={!selectedReceiptDocument}
                    className="w-full rounded-[6px] bg-primary py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:bg-muted disabled:text-text-tertiary"
                  >
                    Generate receipt
                  </button>
                </>
              ) : (
                <div className="space-y-8">
                  <div className="border border-border bg-white p-8 shadow-sm">
                    <div className="mb-12 flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex h-16 w-16 items-center justify-center rounded-[8px] bg-primary text-[24px] font-bold text-white">
                          YST
                        </div>
                        <div>
                          <h3 className="font-heading text-[24px] font-bold leading-tight text-iron-grey">
                            Yeshiva Shaarei Tzion
                          </h3>
                          <p className="mt-1 font-body text-[14px] text-text-secondary">
                            Tuition family receipt
                            <br />
                            Parent portal
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-6 flex items-end justify-between">
                      <h4 className="font-heading text-[32px] font-bold uppercase tracking-tight text-iron-grey">
                        Payment Receipt
                      </h4>
                      <div className="text-right">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
                          <span className="font-body text-text-secondary">
                            Date Issued
                          </span>
                          <span className="font-body font-semibold text-iron-grey">
                            {selectedReceiptIssuedLabel}
                          </span>
                          <span className="font-body text-text-secondary">
                            Status
                          </span>
                          <span className="font-body font-semibold text-primary">
                            Paid
                          </span>
                        </div>
                      </div>
                    </div>

                    <hr className="mb-6 border-t-2 border-iron-grey" />

                    <div className="mb-8">
                      <h5 className="mb-1 font-title text-[11px] font-bold uppercase tracking-wider text-primary">
                        Received From
                      </h5>
                      <p className="font-heading text-[18px] font-bold text-iron-grey">
                        {model.familyName ?? "Family account"}
                      </p>
                    </div>

                    <div className="mb-8">
                      <table className="w-full border-collapse text-left">
                        <thead>
                          <tr className="bg-iron-grey font-body text-[13px] text-white">
                            <th className="px-4 py-2 font-medium">
                              Description
                            </th>
                            <th className="px-4 py-2 font-medium">Document</th>
                            <th className="px-4 py-2 text-right font-medium">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-border font-body text-[14px] text-iron-grey">
                            <td className="px-4 py-3">
                              {selectedReceiptDocument?.detail ||
                                "Payment confirmation"}
                            </td>
                            <td className="px-4 py-3">
                              {selectedReceiptDocument?.label ??
                                "Receipt artifact"}
                            </td>
                            <td className="px-4 py-3 text-right">Ready</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div className="border border-border bg-background-light p-4">
                      <h5 className="mb-2 font-heading text-[14px] font-bold text-iron-grey">
                        PAYMENT INFORMATION
                      </h5>
                      <p className="font-body text-[13px] leading-relaxed text-text-secondary">
                        Receipt artifacts are generated from the tuition ledger
                        and remain family-scoped. Use the download action below
                        for the current document.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        selectedReceiptDocument
                          ? onDownloadDocument(
                              selectedReceiptDocument.document,
                              "receipt-modal-print",
                            )
                          : undefined
                      }
                      className="flex flex-1 items-center justify-center gap-2 rounded-[6px] border border-border-strong bg-surface-light py-3 font-body text-[14px] font-medium text-iron-grey transition-colors hover:bg-muted disabled:opacity-70"
                      disabled={!selectedReceiptDocument || busyAction !== null}
                    >
                      <ReceiptTextIcon className="h-4 w-4" />
                      {busyAction === "receipt-modal-print"
                        ? "Preparing..."
                        : "Print"}
                    </button>
                    <button
                      type="button"
                      data-testid="parent-tuition-receipt-download-1"
                      onClick={() =>
                        selectedReceiptDocument
                          ? onDownloadDocument(
                              selectedReceiptDocument.document,
                              "receipt-modal-download-1",
                            )
                          : undefined
                      }
                      className="flex flex-1 items-center justify-center gap-2 rounded-[6px] bg-primary py-3 font-body text-[14px] font-medium text-white transition-colors hover:bg-primary-hover disabled:opacity-70"
                      disabled={!selectedReceiptDocument || busyAction !== null}
                    >
                      <DownloadIcon className="h-4 w-4" />
                      {busyAction === "receipt-modal-download-1"
                        ? "Preparing..."
                        : "Download"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </DialogShell>
        ) : null}

        {activeDialog === "payors" ? (
          <DialogShell
            title="Manage Payors"
            onClose={onCloseDialog}
            maxWidthClass="max-w-lg"
          >
            <div className="space-y-8">
              {primaryPayor ? (
                <div>
                  <h3 className="mb-3 font-heading text-[13px] font-bold uppercase tracking-wide text-iron-grey">
                    Default Payor
                  </h3>
                  <div
                    data-testid="parent-tuition-payer-row-1"
                    className="flex items-center justify-between rounded-[8px] border border-border bg-background-light p-4"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info-background">
                        <span className="font-heading text-[16px] font-bold text-primary">
                          {primaryPayor.displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-body text-[14px] font-semibold text-iron-grey">
                          {primaryPayor.displayName}
                        </p>
                        <p className="font-body text-[13px] text-text-secondary">
                          {primaryPayor.relationLabel}
                          {primaryPayor.isPrimary
                            ? " (Primary Account Holder)"
                            : ""}
                        </p>
                      </div>
                    </div>
                    <span className="rounded-full border border-[#BDEADD] bg-success-background px-3 py-1 text-[12px] font-medium text-jungle">
                      Active
                    </span>
                  </div>
                </div>
              ) : null}

              <div>
                <h3 className="mb-3 font-heading text-[13px] font-bold uppercase tracking-wide text-iron-grey">
                  Added Payors
                </h3>
                <div
                  data-testid="parent-tuition-payer-list"
                  className="space-y-3"
                >
                  {secondaryPayors.length > 0 ? (
                    secondaryPayors.map((payor, index) => (
                      <div
                        key={payor.id}
                        data-testid={`parent-tuition-payer-row-${index + 2}`}
                        className="flex items-center justify-between rounded-[8px] border border-border bg-surface-light p-4"
                      >
                        <div>
                          <p className="font-body text-[14px] font-semibold text-iron-grey">
                            {payor.displayName}
                          </p>
                          <p className="font-body text-[13px] text-text-secondary">
                            {payor.relationLabel} &bull; {payor.allocationLabel}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-body text-[14px] font-medium text-iron-grey">
                            {payor.balanceLabel}
                          </p>
                          {payor.email ? (
                            <p className="font-body text-[13px] text-text-secondary">
                              {payor.email}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="rounded-[8px] border border-dashed border-border px-4 py-5 text-[14px] text-text-secondary">
                      No additional third-party payors are linked to this
                      tuition account yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-[8px] border border-border bg-background-light p-4 text-[13px] text-text-secondary">
                Payor changes remain read-only on this surface for now. Use the
                business office or the hosted billing workflow if allocation
                ownership needs to change.
              </div>
            </div>
          </DialogShell>
        ) : null}
      </div>
    </div>
  );
}
